import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Download, Edit, X, Plus, SquareArrowUpRightIcon, Send } from 'lucide-react';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsList } from '@/components/ScrollableTabsList';
import type { ClientResponse } from '@/types/client.types';
import { ClientDetailsPeContent } from './ClientDetailsPeContent';
import CaseDetailsPeContent from './CaseDetailsPeContent';
import { WoDetailsPeContent } from './WoDetailsPeContent';
import { AttemptDetailsPeContent } from './AttemptDetailsPeContent';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import Loader from '@/components/Loader';
import { exportProcessServiceWorkOrder } from '@/services/exportProcessServiceWorkOrder';
import { useGetWorkOrderById } from '@/services/workOrderService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { BillingDetailsPeContent } from '@pages/caseManagement/processDetails/BillingDetailsPeContent';
import { ShareAttemptImageDialog } from '@pages/caseManagement/processDetails/ShareAttemptImageDialog';
import AlertBox from '@/components/AlertBox';
import { useSendInvoiceMail } from '@/services/invoiceService';
import { toast } from '@/components/ui/use-toast';
import AffidavitsDetailsPeContent from '@pages/caseManagement/processDetails/AffidavitsDetailsPeContent';
import { ProcessBillingPePanel } from '@pages/caseManagement/processBilling/ProcessBillingPePanel';
import { ProcessBillingVendorPePanel } from '@pages/caseManagement/processBilling/ProcessBillingVendorPePanel';
import {
  getAssignedServersForProcessBilling,
  isProcessBillingServerEligible,
} from '@pages/caseManagement/processBilling/processBillingServerUtils';
import { isProcessBillingVendorEligible } from '@pages/caseManagement/processBilling/processBillingVendorUtils';
import { getProcessBillingServer } from '@/services/processBillingServerService';
import { getProcessBillingVendor } from '@/services/processBillingVendorService';
import { useUserStore } from '@/stores/user';
import { isAdminOrSupervisor as isAdminOrSupervisorUser, canAccessProcessBilling } from '@/services/userService';
import { useQuery } from '@tanstack/react-query';
import { hasFeature } from '@/services/featureService';
import { canCreatePsAttempt } from '@/pages/workOrderAttempts/canCreatePsAttempt';
import { canViewPsAttempt } from '@/pages/workOrderAttempts/canViewPsAttempt';
import { ChangeLogSection } from '@/components/ChangeLogSection';
const iconClasses = 'h-[20px] w-[20px] cursor-pointer';
const ProcessDetails = ({ onCloseClick, id }: { onCloseClick: () => void; id: string }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('CLIENT_DETAILS');
  const [clientData, setClientData] = useState<ClientResponse | null>();
  const { data, isFetching, isError } = useGetWorkOrderById(id);
  const sentInvoiceMail = useSendInvoiceMail();
  const [attemptShareDialogDisplay, setAttemptShareDialogDisplay] = useState(false);
  const [invoiceConfirmationDialog, setInvoiceConfirmationDialog] = useState(false);
  const [displayRecipientEmails, setDisplayRecipientEmails] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const isExportingRef = useRef(false);

  const user = useUserStore((s) => s.user);
  const canEditBilling = isAdminOrSupervisorUser(user);
  const canCreateBilling = canEditBilling || canAccessProcessBilling(user);
  const canCreateVendorBilling = canCreateBilling;
  const canAddAttempt = canCreatePsAttempt(user);
  const canViewHiFirstAttempt = canViewPsAttempt(user);
  const linkedHIWorkOrderID =
    data?.linkedHIWorkOrderID ??
    (data as { linkedHIWorkOrderId?: string | null } | undefined)?.linkedHIWorkOrderId;
  const isProcessBillingEligible = isProcessBillingServerEligible(data);
  const assignedServersCount = data && isProcessBillingEligible ? getAssignedServersForProcessBilling(data).length : 0;
  const billingPanelQuery = useQuery({
    queryKey: ['processBillingServer', id],
    queryFn: () => getProcessBillingServer(id),
    enabled: Boolean(id) && isProcessBillingEligible,
    retry: false,
  });
  const hasBillingBatch = Boolean(billingPanelQuery.data?.processBillingBatchId);
  const showProcessBillingActions = isProcessBillingEligible && assignedServersCount > 0;

  const isProcessBillingVendorReady = Boolean(data && isProcessBillingVendorEligible(data));
  const vendorBillingPanelQuery = useQuery({
    queryKey: ['processBillingVendor', id],
    queryFn: () => getProcessBillingVendor(id),
    enabled: Boolean(id) && isProcessBillingVendorReady,
    retry: false,
  });
  /** Only trust entry shape after a successful GET; on 403/500 still allow Add when we do not know an entry exists. */
  const vendorBillingEntry = vendorBillingPanelQuery.isSuccess ? (vendorBillingPanelQuery.data?.entry ?? null) : null;
  /** True once the vendor-billing GET has settled (success, 404, or any error) so menu CTAs are not hidden on 403/500. */
  const vendorBillingHydrated =
    !vendorBillingPanelQuery.isFetching || vendorBillingPanelQuery.isSuccess || vendorBillingPanelQuery.isError;
  const showVendorBillingAdd =
    isProcessBillingVendorReady && canCreateVendorBilling && vendorBillingHydrated && !vendorBillingEntry;
  const showVendorBillingEdit =
    isProcessBillingVendorReady &&
    vendorBillingHydrated &&
    vendorBillingEntry != null &&
    canEditBilling;

  const tabs = [
    { value: 'CLIENT_DETAILS', label: 'Client Details', display: true },
    { value: 'CASE_DETAILS', label: 'Case Details', display: true },
    { value: 'WO_DETAILS', label: 'W/O Details', display: true },
    { value: 'STATUS_UPDATES', label: 'Status Updates', display: true },
    { value: 'BILLING', label: 'Billing', display: true },
    { value: 'AFFIDAVITS', label: 'Affidavits', display: hasFeature('services.process.affidavit.export') },
    { value: 'CHANGE_LOG', label: 'Change Log', display: user?.roleId !== 'SERVER' },
  ];

  const { displayedTabs, tabWidth } = useMemo(() => {
    const filtered = tabs.filter((tab) => tab.display);
    return {
      displayedTabs: filtered,
      tabWidth: filtered.length <= 3 ? `${100 / filtered.length}%` : '33.33%',
    };
  }, [tabs]);

  const invoiceRecipientEmails = Array.from(
    new Set(
      [
        clientData?.arInvoices,
        clientData?.affidavitWithInvoice,
        clientData?.arInvoicesCc,
        clientData?.affidavitWithInvoiceCc,
        data?.service?.contact?.email,
      ].flatMap((item) => (Array.isArray(item) ? item : item ? [item] : []))
    )
  );

  useEffect(() => {
    if (data?.service?.client) {
      setClientData(data.service.client);
    }
  }, [data]);

  return (
    <Sheet modal={false} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white"
        side="right"
        tabIndex={-1}
      >
        <Loader isLoading={isFetching}>
          {isError ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder">
              <div className="border-[1px] p-[6px] absolute top-6 right-6 rounded-md" onClick={onCloseClick}>
                <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
              </div>
              <span>No Results</span>
            </div>
          ) : (
            <Tabs className="w-full text-black" defaultValue={tabs?.[0]?.value}>
              <SheetHeader className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white">
                <SheetTitle className={`flex flex-col gap-4 bg-white  pb-0`}>
                  <div className="flex flex-row justify-between">
                    <span className="font-semibold text-xl">{`W/O #: ${data?.number}`}</span>
                    <div className="flex flex-row gap-2 h-[32px] [&_div]:border-[1px] [&_div]:p-[6px] [&_div]:rounded-md [&_div]:justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <div>
                            <Send color="#676767" className={iconClasses} />
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="ml-[-100px] min-w-[180px]">
                          {data?.invoice && (
                            <>
                              <DropdownMenuItem>
                                <div
                                  className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                  onClick={() => {
                                    setInvoiceConfirmationDialog(true);
                                  }}
                                >
                                  <span>Email Invoice</span>
                                  <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                </div>
                              </DropdownMenuItem>
                            </>
                          )}
                          {data?.workOrderAttempts?.length && data?.invoice ? (
                            <DropdownMenuSeparator className="h-[2px]" />
                          ) : null}

                          {data?.workOrderAttempts?.length ? (
                            <>
                              <DropdownMenuItem>
                                <div
                                  className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                  onClick={() => setAttemptShareDialogDisplay(true)}
                                >
                                  <span>Email Attempt Updates</span>
                                  <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                </div>
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <div
                        onClick={async () => {
                          if (isExportingRef.current) return;
                          isExportingRef.current = true;
                          setIsExporting(true);
                          try {
                            await exportProcessServiceWorkOrder({
                              workOrderId: id,
                              workOrderNumber: data?.number,
                              linkedHIWorkOrderID,
                            });
                          } finally {
                            isExportingRef.current = false;
                            setIsExporting(false);
                          }
                        }}
                        className={`h-[34px] ${isExporting ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                        title="Export"
                      >
                        <Download color="#676767" className={iconClasses} />
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          {data?.workOrderAttempts?.length ? (
                            <div>
                              <Edit color="#676767" className={iconClasses} />
                            </div>
                          ) : activeTab === 'STATUS_UPDATES' && canAddAttempt ? (
                            <div>
                              <Plus color="#676767" className={iconClasses} />
                            </div>
                          ) : (
                            <div>
                              <Edit color="#676767" className={iconClasses} />
                            </div>
                          )}
                        </DropdownMenuTrigger>

                        <DropdownMenuContent className="ml-[-100px] min-w-[180px]">
                          <>
                            {activeTab === 'STATUS_UPDATES' &&
                              ((data?.workOrderAttempts?.length ?? 0) > 0 || (data?.server ?? data?.vendor)) && (
                                <>
                                  {(data?.server ?? data?.vendor) && canAddAttempt && (
                                    <>
                                      <DropdownMenuItem>
                                        <div
                                          className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                          onClick={() =>
                                            navigate(
                                              `workorder/${data?.workOrderId}/attempts?mode=edit&attemptMode=create`
                                            )
                                          }
                                        >
                                          <span>Add Attempt</span>
                                          <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                        </div>
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator className="h-[2px]" />
                                    </>
                                  )}
                                  {data?.workOrderAttempts?.length ? (
                                    <>
                                      <DropdownMenuItem>
                                        <div
                                          className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                          onClick={() => navigate(`workorder/${data?.workOrderId}/attempts?mode=edit`)}
                                        >
                                          <span>Edit Attempt</span>
                                          <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                        </div>
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator className="h-[2px]" />
                                    </>
                                  ) : null}
                                </>
                              )}
                            {activeTab === 'BILLING' && (
                              <>
                                <DropdownMenuItem>
                                  <div
                                    className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                    onClick={() => {
                                      navigate(
                                        `/process/invoice/${data?.invoice?.invoiceNumber}/update?workOrderId=${data?.workOrderId}`
                                      );
                                    }}
                                  >
                                    <span>Edit Invoice</span>
                                    <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                  </div>
                                </DropdownMenuItem>
                              </>
                            )}
                            {showProcessBillingActions && hasBillingBatch && canEditBilling ? (
                              <DropdownMenuItem>
                                <div
                                  className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                  onClick={() =>
                                    navigate(`/process/workorder/${data?.workOrderId}/process-billing/edit`)
                                  }
                                >
                                  <span>Edit Billing Entry</span>
                                  <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                </div>
                              </DropdownMenuItem>
                            ) : showProcessBillingActions && !hasBillingBatch && canCreateBilling ? (
                              <DropdownMenuItem>
                                <div
                                  className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                  onClick={() =>
                                    navigate(`/process/workorder/${data?.workOrderId}/process-billing/create`)
                                  }
                                >
                                  <span>Add Server Billing</span>
                                  <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                </div>
                              </DropdownMenuItem>
                            ) : null}
                            {showVendorBillingEdit ? (
                              <DropdownMenuItem>
                                <div
                                  className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                  onClick={() =>
                                    navigate(`/process/workorder/${data?.workOrderId}/process-billing-vendor/edit`)
                                  }
                                >
                                  <span>Edit Vendor Billing Entry</span>
                                  <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                </div>
                              </DropdownMenuItem>
                            ) : showVendorBillingAdd ? (
                              <DropdownMenuItem>
                                <div
                                  className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                  onClick={() =>
                                    navigate(`/process/workorder/${data?.workOrderId}/process-billing-vendor/create`)
                                  }
                                >
                                  <span>Add Vendor Billing</span>
                                  <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                                </div>
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuItem>
                              <div
                                className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                                onClick={() => {
                                  const tabLabel = tabs.find((tab) => tab.value === activeTab)?.label;
                                  if (tabLabel && ['W/O Details', 'Case Details', 'Client Details'].includes(tabLabel))
                                    navigate(
                                      `${data?.serviceId}/update?stepper=${`${tabLabel === 'Status Updates' ? 'W/O Details' : tabLabel}`}&workOrderId=${data?.workOrderId}`
                                    );
                                  else navigate(`${data?.serviceId}/update?workOrderId=${data?.workOrderId}`);
                                }}
                              >
                                <span>Edit Work Order</span>
                                <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                              </div>
                            </DropdownMenuItem>
                          </>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <div onClick={onCloseClick} className="h-[34px]">
                        <X color="#676767" className={iconClasses} />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-2">
                    <div className="flex flex-row gap-2">
                      <div className="bg-[#9747FF33] rounded-md text-normal font-semibold p-2 h-[48px] w-[48px] flex justify-center text-[#55288F] items-center">
                        <span>
                          {clientData?.type === 'INDIVIDUAL'
                            ? `${clientData?.firstName?.[0] ?? ''}${clientData?.lastName?.[0] ?? ''}`
                            : clientData?.clientName?.[0]}
                        </span>
                      </div>

                      <div
                        className="flex flex-col"
                        style={{
                          overflowWrap: 'anywhere',
                        }}
                      >
                        <span className="text-base font-medium">{clientData?.clientName}</span>
                        <span className="text-sm font-normal">#{clientData?.code}</span>
                      </div>
                    </div>
                    {linkedHIWorkOrderID ? (
                      <div className="h-[20px] px-3 text-xs font-medium rounded-full flex items-center justify-center bg-[#EDF5FF] text-primary shrink-0 whitespace-nowrap">
                        Includes Home Inspection
                      </div>
                    ) : null}
                  </div>
                  <div className="text-[#969696] font-[400] flex flex-col text-sm">
                    <span
                      style={{
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {[data?.address1, data?.address2].filter(Boolean).join(', ')}
                    </span>
                    <span>{[data?.city, data?.state, data?.zipCode].filter(Boolean).join(', ')}</span>
                  </div>
                  
                </SheetTitle>
                <ScrollableTabsList className="!mt-0">
                  {displayedTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      style={{ width: tabWidth }}
                      className="flex-none "
                      autoFocus={tab.value === 'CLIENT_DETAILS'}
                      onClick={() => setActiveTab(tab.value)}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </ScrollableTabsList>
              </SheetHeader>

              <div className={`w-full !mt-0 px- pb-2 text-[#0A0A0A]`}>
                <TabsContent value="CLIENT_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <ClientDetailsPeContent data={data} />
                </TabsContent>
                <TabsContent value="CASE_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <CaseDetailsPeContent data={data?.service?.case} />
                </TabsContent>
                <TabsContent value="WO_DETAILS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <WoDetailsPeContent data={data} />
                </TabsContent>
                <TabsContent value="STATUS_UPDATES" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <AttemptDetailsPeContent
                    workOrderId={data?.workOrderId}
                    attemptData={data?.workOrderAttempts ? [...data.workOrderAttempts].reverse() : []}
                    emailLogs={data?.emailLogs ?? []}
                    firstAttemptId={data?.workOrderAttempts?.[0]?.attemptId}
                    linkedToHomeInspection={Boolean(linkedHIWorkOrderID)}
                    canViewHiFirstAttempt={canViewHiFirstAttempt}
                  />
                </TabsContent>
                <TabsContent value="BILLING" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <BillingDetailsPeContent billingData={data?.invoice} workOrderId={data?.workOrderId} />
                  {isProcessBillingVendorEligible(data) ? (
                    <ProcessBillingVendorPePanel workOrder={data} />
                  ) : (
                    <ProcessBillingPePanel workOrder={data} />
                  )}
                </TabsContent>
                <TabsContent value="AFFIDAVITS" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <AffidavitsDetailsPeContent
                    workOrderId={data?.workOrderId}
                    workOrderNumber={data?.number}
                    exportCompleteAffidavit={(data?.workOrderAttempts?.length ?? 0) > 0}
                  />
                </TabsContent>
                {data?.workOrderId && user?.roleId !== 'SERVER' && (
                  <TabsContent value="CHANGE_LOG" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                    <ChangeLogSection
                      target={{ recordType: 'WORK_ORDER', serviceType: 'PROCESS_SERVICE', recordId: data.workOrderId }}
                      active={activeTab === 'CHANGE_LOG'}
                      className="px-6"
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          )}
        </Loader>
        {attemptShareDialogDisplay && data?.workOrderAttempts?.length ? (
          <ShareAttemptImageDialog
            onCloseClick={() => setAttemptShareDialogDisplay(false)}
            attemptIds={data.workOrderAttempts.map((attempt) => attempt.attemptId)}
            workOrderId={data.workOrderId}
            recipientEmail={data.service.contact.email}
          />
        ) : null}
        {invoiceConfirmationDialog && (
          <AlertBox
            title="Are you sure?"
            description={
              <div>
                <span>Are you sure you want to send the invoice? </span>
                {displayRecipientEmails ? (
                  <div className="flex flex-col gap-2">
                    <span>{invoiceRecipientEmails.filter((email) => email).join(', ')}</span>
                    <span onClick={() => setDisplayRecipientEmails(false)} className="text-primary cursor-pointer">
                      Hide Recipients
                    </span>
                  </div>
                ) : (
                  <span onClick={() => setDisplayRecipientEmails(true)} className="text-primary cursor-pointer">
                    View Recipients
                  </span>
                )}
              </div>
            }
            isLoading={sentInvoiceMail.isPending}
            open={invoiceConfirmationDialog}
            cancelBtnLabel="No"
            confirmBtnLabel="Yes"
            cancelBtnClick={() => {
              setInvoiceConfirmationDialog(false);
              setDisplayRecipientEmails(false);
            }}
            confirmBtnClick={() => {
              sentInvoiceMail.mutate(
                { invoiceNumber: data?.invoice?.invoiceNumber ?? '' },
                {
                  onSuccess: (data) => {
                    toast({
                      description: data.message,
                    });
                    setInvoiceConfirmationDialog(false);
                    setDisplayRecipientEmails(false);
                  },
                  onError: (error) => {
                    toast({
                      description: error.response?.data.error.message ?? 'Failed to send invoice email.',
                      variant: 'destructive',
                    });
                    setInvoiceConfirmationDialog(false);
                    setDisplayRecipientEmails(false);
                  },
                }
              );
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ProcessDetails;
